/**
 * Thin API client — wraps fetch with credentials and base URL.
 * In demo builds, all traffic is served by the in-browser mock.
 */

import { demoRequest } from '../demo/demoApi.js';

const BASE = import.meta.env.VITE_API_URL ?? '';
const DEMO = true; // this project is the managed-theater demo

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<{ data: T | null; status: number; ok: boolean }> {
  if (DEMO) {
    return demoRequest<T>(method, path, body);
  }

  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  });

  let data: T | null = null;
  try {
    data = await res.json();
  } catch {
    // empty body
  }

  return { data, status: res.status, ok: res.ok };
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

// ── Typed helpers ──────────────────────────────────────────────────────────

import type { ApiUser } from '@contactrate/shared';

export interface LoginResult extends ApiUser {}

export async function login(email: string, password: string): Promise<LoginResult> {
  const { data, ok, status } = await api.post<LoginResult>('/auth/login', { email, password });
  if (!ok || !data) throw new Error(`Login failed (${status})`);
  return data;
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout', {});
}

export async function getMe(): Promise<ApiUser | null> {
  const { data, ok } = await api.get<ApiUser>('/me');
  return ok ? data : null;
}
